import type React from 'react'

import { isValidElement } from 'react'

import { BodyMediumSubtext, BodyXS, FontWeight } from '@/components/atomic/text'

export const SummaryItemWrapper = ({
  children,
}: {
  children: React.ReactNode
}) => <div className="w-full">{children}</div>

export const SummaryItemLabel = ({ label }: { label: string }) => (
  <BodyXS className="text-secondary truncate">{label}</BodyXS>
)

export const SummaryItemValue = ({
  value,
}: {
  value: string | number | React.ReactNode
}) => {
  if (isValidElement(value)) {
    return value
  }

  return (
    <BodyMediumSubtext
      fontWeight={FontWeight.Medium}
      className="capitalize truncate pt-1.5 pb-0.5"
    >
      {value}
    </BodyMediumSubtext>
  )
}

export const SummaryGridContainer = ({
  children,
  columns,
  sizeColumnsToContent = false,
}: {
  children: React.ReactNode
  columns: number
  sizeColumnsToContent?: boolean
}) => {
  // Default: equal-width columns. Opt-in `sizeColumnsToContent` uses
  // minmax(max-content, 1fr) so columns grow to fit content (long values like
  // "Marketable limit" don't truncate, short ones don't waste space) while
  // leftover space still distributes evenly. Only safe when total content
  // fits the container — left as a per-consumer choice to avoid overflow on
  // grids with long values (e.g. bond issuer names).
  const trackSize = sizeColumnsToContent
    ? 'minmax(max-content, 1fr)'
    : 'minmax(0, 1fr)'
  const gridTemplateColumns = `repeat(${columns}, ${trackSize})`

  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: Bulk suppression as part of biome migration
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns }}
      aria-label="Summary"
    >
      {children}
    </div>
  )
}

export const SummaryGridItem = ({
  children,
}: {
  children: React.ReactNode
}) => <div className="overflow-hidden">{children}</div>
